import { Box, CircularProgress, Fade, Stack, Typography, useTheme } from "@mui/material";
import { PureComponent, useCallback, useMemo, useRef, useState } from "react";
import { GUTTER_SIZE } from "../Common/drive-asset-grid";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { authorizedObjectOnPanel } from "../Common/drive-center-panel";
import {
  GetCurrentObj,
  GetIsSearch,
  GetObjectResults,
  GetPagination,
  GetPanelObject,
  GetSearchResults,
  SetPanelObject,
} from "../../../Redux/Reducers/redux-slice-drive";
import AssetCard from "../../Cards/AssetCard/asset-card";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import Panel from "../../Panels/Panel/panel";
import { CHILDREN_PAGE_SIZE, useFolderPagination, useSearchPagination } from "../helpers/use-drive-pagination";

export const DriveFolderPanel: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(100);
  const currentObject = useAppSelector(GetCurrentObj);
  const panelObject = useAppSelector(GetPanelObject);
  const searchResults = useAppSelector(GetSearchResults);
  const folderResults = useAppSelector(GetObjectResults);
  const isSearch = useAppSelector(GetIsSearch);

  const folderViewAssets = useMemo(() => {
    return isSearch ? [...searchResults] : [...folderResults];
  }, [isSearch, searchResults, folderResults]);

  const { loadFolderContents } = useFolderPagination();
  const { loadSearchContent } = useSearchPagination();
  const { totalChildrenCount, loadedChildrenCount, hasNextPage, isNextPageLoading } = useAppSelector(GetPagination);

  const loadMoreItems = (startIndex, stopIndex) => {
    if (isNextPageLoading) {
      return;
    }

    return isSearch
      ? loadSearchContent({ page: Math.floor(loadedChildrenCount / CHILDREN_PAGE_SIZE), pageSize: CHILDREN_PAGE_SIZE })
      : loadFolderContents({
          page: Math.floor(loadedChildrenCount / CHILDREN_PAGE_SIZE),
          pageSize: CHILDREN_PAGE_SIZE,
        });
  };

  const resizeObserver = useRef<ResizeObserver>(
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries[0].contentBoxSize[0].inlineSize && setWidth(entries[0].contentBoxSize[0].inlineSize);
      entries[0].contentBoxSize[0].blockSize && setHeight(entries[0].contentBoxSize[0].blockSize);
    })
  );

  const listContainerRef = useCallback(
    (container: HTMLDivElement) => {
      if (container !== null) {
        resizeObserver.current.observe(container);
      }
      // When element is unmounted, ref callback is called with a null argument
      // => best time to cleanup the observer
      else {
        if (resizeObserver.current) resizeObserver.current.disconnect();
      }
    },
    [resizeObserver.current]
  );

  const handleCardClick = (obj: DbAssetObjectInfos) => {
    if (authorizedObjectOnPanel.includes(obj.type)) {
      dispatch(SetPanelObject(obj));
    }
  };

  return (
    <Panel
      header={
        <Stack
          p={3}
          direction="row"
          alignItems="center"
          sx={(theme) => ({
            position: "sticky",
            top: 0,
            left: 0,
            height: "46px",
            width: "100%",
            background: theme.palette.background.default,
            zIndex: theme.zIndex.appBar - 1,
          })}
        >
          <Typography
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            variant="subtitle2"
            fontWeight={500}
            sx={{ flexGrow: 1, textWrap: "nowrap" }}
          >
            {isSearch ? <>Search results</> : <>{currentObject && currentObject.name}</>}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            {totalChildrenCount}
          </Typography>
        </Stack>
      }
      noWrapper
      open
      position="left"
      sx={{ height: "100%", overflow: "hidden" }}
    >
      <Stack height="calc(100% - 88px)">
        <Box ref={listContainerRef} sx={(t) => ({ display: "block", width: "100%", height: "100%" })}>
          <InfiniteLoader
            loadMoreItems={loadMoreItems}
            isItemLoaded={(index) => index < loadedChildrenCount}
            itemCount={hasNextPage ? loadedChildrenCount + 1 : loadedChildrenCount}
          >
            {({ onItemsRendered, ref }) => (
              <FixedSizeList
                ref={ref}
                itemSize={width / (16 / 9) + 60}
                height={height}
                width={width}
                // width={width}
                itemCount={hasNextPage ? loadedChildrenCount + 1 : loadedChildrenCount}
                style={{
                  overflowY: "scroll",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${
                    theme.palette.mode === "dark" ? theme.palette.background[200] : theme.palette.background[400]
                  } transparent`,
                }}
                itemData={{
                  assets: folderViewAssets,
                  panelObject,
                  //   handleNameChanged,
                  onCardClick: handleCardClick,
                  loadingNextPageIndex: hasNextPage ? loadedChildrenCount : -1,
                  isItemLoaded: (index) => !hasNextPage || index < loadedChildrenCount,
                }}
                onItemsRendered={onItemsRendered}
              >
                {AssetCardRenderer}
              </FixedSizeList>
            )}
          </InfiniteLoader>
        </Box>
      </Stack>
    </Panel>
  );
};

class AssetCardRenderer extends PureComponent<any> {
  render() {
    const { index, style, data } = this.props as any;

    // Access the data source using the "data" prop:
    const { assets, panelObject, handleNameChanged, onCardClick, loadingNextPageIndex, isItemLoaded } = data;

    if (index === loadingNextPageIndex)
      return (
        <div
          style={{
            ...style,
            paddingBottom: GUTTER_SIZE,
            paddingLeft: GUTTER_SIZE,
            paddingRight: GUTTER_SIZE,
            position: style.position,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="assetGridSingle"
          key={`grid-loader-${index}`}
        >
          <CircularProgress size={36} />
        </div>
      );

    const obj = assets[index];

    if (!obj) return <></>;

    return (
      <Fade in={isItemLoaded(index)} timeout={500}>
        <div
          style={{
            ...style,
            paddingBottom: GUTTER_SIZE,
            paddingLeft: GUTTER_SIZE,
            paddingRight: GUTTER_SIZE,
            position: style.position,
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
          }}
          className="assetGridSingle"
          key={`grid-card-${index}`}
        >
          <AssetCard
            key={obj.id}
            labelList={[obj.type]}
            selectMode={false}
            active={panelObject?.id === obj.id}
            onCardClick={onCardClick}
            //   onNameChanged={(name) => onNameChanged(obj.id, name)}
            showDriveActions={false}
            obj={obj}
          />
        </div>
      </Fade>
    );
  }
}
